import type { CaptureContext } from '@sentry/core';
import {
  withScope,
  captureException,
  captureMessage,
  isInitialized,
  setUser,
  getCurrentScope,
} from '@sentry/react';

import { SENTRY_DSN, isRNWebview } from 'constants/constants';
import { isOnlineSelector } from 'selectors/tradeSelectors';
import IUser from 'types/IUser';

import StoreProxy from '../storeProxy';
import { devOnlyErrorLogger } from './logger';
import { trackMixpanelEvent } from './mixpanel-init';

/**
 * @param userObj: Partial<IUser> | null,
 * @param logEntry: string | Error | object,
 * @param captureContext?: CaptureContext, pass any custom object values in "extra" key of this object. extra object shows up in "Additional Data" section of a sentry dashboard event
 * @param tags?: Record<string, string>
 * @example
 * addLogsToSentry(userObj, new Error('test'), { extra: anyCustomProperties }, { sentryCustomTag: 'value' });
 */
export const addLogsToSentry = (
  userObj: Partial<IUser> | null,
  logEntry: string | Error | object,
  captureContext?: CaptureContext,
  tags?: Record<string, string>
) => {
  devOnlyErrorLogger('addLogsToSentry', logEntry, captureContext, tags);

  const state = StoreProxy.getState();
  const isOnline = isOnlineSelector(state);
  withScope(scope => {
    if (userObj) {
      scope.setUser({
        id: userObj.id,
        username: userObj.userName,
        email: userObj.email,
      });
    }
    if (tags) {
      scope.setTags(tags);
    }
    scope.setTag('isOnline', isOnline);
    if (typeof logEntry === 'object') {
      if (logEntry instanceof Error) {
        captureException(logEntry, captureContext);
        return;
      }
      captureException(new Error(JSON.stringify(logEntry)), captureContext);
    } else {
      captureMessage(logEntry, captureContext);
    }
  });
};

export const addLogsInSentryAndMixpanel = (
  userObj: Partial<IUser>,
  title: string,
  captureContext?: CaptureContext
) => {
  trackMixpanelEvent(title, captureContext?.extra ?? {});

  addLogsToSentry(userObj, title, captureContext);
};

export const setLoggedInInSentry = (userObj: Partial<IUser>) => {
  if (!SENTRY_DSN || !isInitialized()) return;

  const loggedInUser = {
    id: userObj.id,
    username: userObj.userName,
    email: userObj.email,
    source: isRNWebview() ? 'ReactNative' : 'WebApp',
  };
  setUser(loggedInUser);
  getCurrentScope().setTag('isUserLoggedin', 'true');
};

export const setLoggedOutInSentry = () => {
  if (!SENTRY_DSN || !isInitialized()) return;

  const loggedOutUser = {
    source: isRNWebview() ? 'ReactNative' : 'WebApp',
    environment: process.env.REACT_APP_ENV,
  };
  setUser(loggedOutUser);
  getCurrentScope().setTag('isUserLoggedin', 'false');
};

// Add this helper function before the class or inside the class
export const isNetworkError = error => {
  // Abort errors (code 20 = DOMException.ABORT_ERR) are expected when:
  // - User navigates away quickly
  // - Multiple rapid requests trigger abort of previous request
  // - Component unmounts during fetch
  // These should not be logged to Sentry
  if (
    error?.name === 'AbortError' ||
    error?.code === 20 ||
    error?.message?.includes('AbortError') ||
    error?.aborted === true
  ) {
    return true;
  }

  const networkErrorPatterns = [
    'Failed to fetch',
    'Network request failed',
    'NetworkError',
    'net::ERR_',
    'Load failed',
    'The Internet connection appears to be offline',
    'A server with the specified hostname could not be found',
  ];

  const errorMessage = error?.message || error?.toString() || '';

  // Network errors typically don't have an HTTP status code
  // If error has a status, it's a backend response, not a network failure
  if (error?.status) {
    return false;
  }

  return networkErrorPatterns.some(pattern => errorMessage.includes(pattern));
};
